import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

// Linked from:
//  - IV report-ready emails (QR code → app.inspect.ca/install?code=<icLinkToken>)
//  - PI public viewer's "Open in Inspect Canada" button
//  - Chottu Link fallback path
// Before 2026-06-07 this route returned 404; emails + PI shares were silently broken.

// App Store ID matches the live App Store Connect entry for bundle
// com.inspectcanada (verified via App Store Connect API 2026-06-10).
// Note: as of 2026-06-10 the v1.0 build is still READY_FOR_REVIEW —
// the link will 404/region-error until Apple approves the Unlisted
// submission. TestFlight Public Link below is the working path in the
// meantime.
const APP_STORE_URL = 'https://apps.apple.com/ca/app/inspect-canada/id6771061626';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.inspectcanada';

// Flip this to `true` AFTER Play Production is approved + the listing is live.
// While false: Android auto-redirect goes to the Internal Test opt-in URL so
// testers don't land on Play Store's "Item not found" page. (Reza hit this
// 2026-06-11.)
const PLAY_PRODUCTION_LIVE = false;

// Beta-tester install paths — used by testers outside Canada (the App Store
// Unlisted distribution is region-locked to CA right now) and by Android
// testers who didn't receive the Play Console invite email.
//
// Fill these in from:
//   - iOS:     App Store Connect → TestFlight → Public Link → "Enable"
//              → copy the testflight.apple.com/join/XXXXXXXX URL
//   - Android: Play Console → Testing → Internal Testing → Testers tab
//              → "How testers join your test" → "Copy link"
//
// Leave a value as the empty string to hide that button. The whole Beta
// Testers card hides if BOTH are empty.
const TESTFLIGHT_PUBLIC_URL = 'https://testflight.apple.com/join/UqGgk91X';
const PLAY_INTERNAL_OPT_IN_URL =
  'https://play.google.com/apps/internaltest/4701149125496214144';

function detectPlatform(): 'ios' | 'android' | 'desktop' {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent.toLowerCase();
  if (/iphone|ipad|ipod/.test(ua)) return 'ios';
  if (/android/.test(ua)) return 'android';
  return 'desktop';
}

export default function InstallPage() {
  const [params] = useSearchParams();
  const code = params.get('code') ?? '';
  const [platform, setPlatform] = useState<'ios' | 'android' | 'desktop'>('desktop');

  useEffect(() => {
    const p = detectPlatform();
    setPlatform(p);
    if (p === 'ios') {
      const t = setTimeout(() => {
        window.location.replace(APP_STORE_URL);
      }, 700);
      return () => clearTimeout(t);
    }
    if (p === 'android') {
      const target = PLAY_PRODUCTION_LIVE ? PLAY_STORE_URL : PLAY_INTERNAL_OPT_IN_URL;
      const t = setTimeout(() => {
        window.location.replace(target);
      }, 700);
      return () => clearTimeout(t);
    }
  }, []);

  return (
    <section className="max-w-2xl mx-auto px-5 py-16 text-center">
      <img
        src="/ic-logo.png"
        alt="Inspect Canada"
        className="h-12 w-auto mx-auto"
      />
      <h1 className="mt-6 text-3xl font-semibold text-navy">
        Install Inspect Canada
      </h1>
      <p className="mt-3 text-slate-700 max-w-md mx-auto">
        Get the app to open your inspection report, browse Property Insights,
        book a strata review, and chat with our AI Home Consultant.
      </p>

      {platform !== 'desktop' && (
        <p className="mt-4 text-sm text-slate-500">
          Redirecting you to the {platform === 'ios'
            ? 'App Store'
            : PLAY_PRODUCTION_LIVE ? 'Google Play Store' : 'beta tester opt-in page'}…
        </p>
      )}

      <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
        <a
          href={APP_STORE_URL}
          className="inline-flex items-center justify-center rounded-xl bg-navy text-white px-6 py-3 font-semibold hover:opacity-90"
          target="_blank"
          rel="noreferrer"
        >
          Download for iPhone
        </a>
        <a
          href={PLAY_STORE_URL}
          className="inline-flex items-center justify-center rounded-xl bg-navy text-white px-6 py-3 font-semibold hover:opacity-90"
          target="_blank"
          rel="noreferrer"
        >
          Download for Android
        </a>
      </div>

      {code ? (
        <p className="mt-6 text-xs text-slate-500">
          Linked to your inspector — code preserved through install.
        </p>
      ) : null}

      {(TESTFLIGHT_PUBLIC_URL || PLAY_INTERNAL_OPT_IN_URL) && (
        <div className="mt-12 mx-auto max-w-md rounded-2xl border border-slate-200 bg-slate-50 p-6 text-left">
          <h2 className="text-base font-semibold text-navy">
            Beta tester?
          </h2>
          <p className="mt-2 text-sm text-slate-600">
            Use these links if the storefront says the app isn't available
            in your country, or if you didn't receive the tester invitation
            email.
          </p>
          <div className="mt-4 flex flex-col gap-2">
            {TESTFLIGHT_PUBLIC_URL && (
              <a
                href={TESTFLIGHT_PUBLIC_URL}
                className="inline-flex items-center justify-center rounded-lg border border-navy bg-white text-navy px-4 py-2.5 text-sm font-semibold hover:bg-slate-100"
                target="_blank"
                rel="noreferrer"
              >
                Join TestFlight (iPhone)
              </a>
            )}
            {PLAY_INTERNAL_OPT_IN_URL && (
              <a
                href={PLAY_INTERNAL_OPT_IN_URL}
                className="inline-flex items-center justify-center rounded-lg border border-navy bg-white text-navy px-4 py-2.5 text-sm font-semibold hover:bg-slate-100"
                target="_blank"
                rel="noreferrer"
              >
                Join Android beta
              </a>
            )}
          </div>
          <p className="mt-3 text-xs text-slate-500">
            On Android, tap "Become a tester" then return to Play Store to install.
          </p>
        </div>
      )}
    </section>
  );
}
